import { useMemo } from 'react';
import { useGetCampaignLeaderboard } from '@/hooks/challenges';
import { CAMPAIGN_ID, CAMPAIGN_GROUP_ID } from '@/config/campaign';
import { LeaderboardPeriodType } from '@/types/challenges.types';

export interface LeaderboardUser {
  rank: number;
  name?: string;
  email?: string;
  address?: string;
  username?: string;
  avatar?: string;
  points: number;
}

export const useLeaderboard = (
  address: string = '',
  limit: number = 5,
  currentPage: number = 0,
  periodType: LeaderboardPeriodType = LeaderboardPeriodType.ALL
) => {
  const { data: leaderboardData, isLoading } = useGetCampaignLeaderboard(
    CAMPAIGN_ID,
    CAMPAIGN_GROUP_ID || '',
    address,
    limit,
    periodType,
    currentPage
  );

  const leaderboard = useMemo(() => {
    if (!leaderboardData?.data) return [];
    
    return leaderboardData.data.map((entry: any, index: number) => {
      const globalRank = currentPage * limit + index + 1;
      
      return {
        rank: globalRank,
        name: entry.name, 
        username: entry.username, 
        email: entry.email,
        address: entry.address,
        avatar: entry.avatar,
        points: parseFloat(entry.totalPoints || entry.points || 0)
      } as LeaderboardUser;
    });
  }, [leaderboardData, currentPage, limit]);

  return {
    leaderboard,
    isLoading,
    hasNextPage: leaderboard.length === limit,
    hasPrevPage: currentPage > 0
  };
};

export const truncateAddress = (address: string) => {
  if (!address) return 'Anonymous';
  if (address.startsWith('0x') && address.length > 10) {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  }
  return address;
};

export const getDisplayName = (user: LeaderboardUser) => {
  if (user?.name) {
    return user.name;
  }
  
  if (user?.username) {
    return user.username;
  }
  
  if (user?.email?.includes('@')) {
    return user.email.split('@')[0];
  }
  
  return truncateAddress(user?.address || 'Anonymous');
};